'use client';
import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import MobilePartnerLayout from '@/components/MobilePartnerLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface Lead {
  id: string;
  name: string;
  phone: string | null;
  stage: string;
  kavling_name: string | null;
  project_name: string | null;
  source: string | null;
  created_at: string;
}

interface Project {
  id: string;
  name: string;
}

interface Plot {
  id: string;
  plot_number: string;
  block: string | null;
  status: string;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Baru saja';
  if (mins < 60) return `${mins} menit lalu`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} jam lalu`;
  return `${Math.floor(hrs / 24)} hari lalu`;
}

function getStatusStyle(stage: string) {
  switch (stage) {
    case 'New': return { cls: 'bg-blue-100 text-blue-700', label: 'Baru' };
    case 'Contacted': return { cls: 'bg-purple-100 text-purple-700', label: 'Dihubungi' };
    case 'Survey': return { cls: 'bg-yellow-100 text-yellow-700', label: 'Survey' };
    case 'Booking': return { cls: 'bg-orange-100 text-orange-700', label: 'Booking' };
    case 'Closing': return { cls: 'bg-green-100 text-green-700', label: 'Closing' };
    default: return { cls: 'bg-muted text-muted-foreground', label: stage };
  }
}

const ALL_STAGES = ['New', 'Contacted', 'Survey', 'Booking', 'Closing'];

interface UpdateStatusSheetProps {
  lead: Lead;
  onClose: () => void;
  onUpdated: (id: string, stage: string) => void;
}

function UpdateStatusSheet({ lead, onClose, onUpdated }: UpdateStatusSheetProps) {
  const [saving, setSaving] = useState(false);
  const supabase = createClient();

  const handleUpdate = async (stage: string) => {
    setSaving(true);
    const { error } = await supabase.from('leads').update({ stage }).eq('id', lead.id);
    if (!error) {
      onUpdated(lead.id, stage);
      onClose();
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card rounded-t-3xl shadow-2xl pb-8">
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-border" />
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div>
            <span className="text-base font-bold text-foreground">Update Status</span>
            <p className="text-xs text-muted-foreground">{lead.name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground">
            <Icon name="XMarkIcon" size={20} />
          </button>
        </div>
        <div className="px-5 pt-4 space-y-2">
          {ALL_STAGES.map((stage) => {
            const style = getStatusStyle(stage);
            const isCurrent = lead.stage === stage;
            return (
              <button
                key={stage}
                onClick={() => handleUpdate(stage)}
                disabled={saving || isCurrent}
                className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl border transition-all ${
                  isCurrent ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${style.cls}`}>{style.label}</span>
                </div>
                {isCurrent && <Icon name="CheckCircleIcon" size={18} className="text-primary" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

interface AddLeadSheetProps {
  onClose: () => void;
  onAdded: (lead: Lead) => void;
  agentId: string | null;
  agentCode: string | null;
  agentEmail: string | null;
  agentName: string | null;
  onPointsEarned: () => void;
}

function AddLeadSheet({ onClose, onAdded, agentId, agentCode, agentEmail, agentName, onPointsEarned }: AddLeadSheetProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [plots, setPlots] = useState<Plot[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedPlotId, setSelectedPlotId] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingPlots, setLoadingPlots] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const supabase = createClient();

  // Fetch projects on mount
  useEffect(() => {
    const fetchProjects = async () => {
      setLoadingProjects(true);
      const { data } = await supabase
        .from('projects')
        .select('id, name')
        .eq('status', 'ACTIVE')
        .order('name');
      if (data) setProjects(data as Project[]);
      setLoadingProjects(false);
    };
    fetchProjects();
  }, []);

  // Fetch plots when project changes
  useEffect(() => {
    if (!selectedProjectId) {
      setPlots([]);
      setSelectedPlotId('');
      return;
    }
    const fetchPlots = async () => {
      setLoadingPlots(true);
      setSelectedPlotId('');
      const { data } = await supabase
        .from('plots')
        .select('id, plot_number, block, status')
        .eq('project_id', selectedProjectId)
        .in('status', ['AVAILABLE', 'HOLD'])
        .order('block')
        .order('plot_number');
      if (data) setPlots(data as Plot[]);
      setLoadingPlots(false);
    };
    fetchPlots();
  }, [selectedProjectId]);

  const selectedProject = projects.find((p) => p.id === selectedProjectId);
  const selectedPlot = plots.find((p) => p.id === selectedPlotId);

  const getPlotLabel = (plot: Plot) =>
    plot.block ? `${plot.block}-${plot.plot_number}` : plot.plot_number;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const kavlingName = selectedPlot
        ? getPlotLabel(selectedPlot)
        : null;
      const projectName = selectedProject?.name || null;

      const { data, error: err } = await supabase
        .from('leads')
        .insert({
          name: name.trim(),
          phone: phone.trim(),
          kavling_name: kavlingName,
          kavling_id: selectedPlotId || null,
          project_name: projectName,
          stage: 'New',
          source: 'Lainnya',
          agent_id: agentId || null,
          agent_code: agentCode || null,
          privacy_consent: true,
        })
        .select()
        .single();
      if (err) throw err;

      // Send confirmation email to agent (fire-and-forget)
      if (agentEmail) {
        const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
        const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
        if (supabaseUrl && anonKey) {
          fetch(`${supabaseUrl}/functions/v1/send-lead-notification`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${anonKey}`,
            },
            body: JSON.stringify({
              agent_email: agentEmail,
              agent_name: agentName || agentCode,
              prospect_name: name.trim(),
              prospect_phone: phone.trim(),
              property_name: kavlingName || 'Belum ditentukan',
              project_name: projectName,
              channel: 'Lainnya',
              notes: null,
            }),
          }).catch(() => {});
        }
      }

      onAdded(data as Lead);
      onPointsEarned();
      onClose();
    } catch (e: any) {
      setError(e.message || 'Gagal menyimpan lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-card rounded-t-3xl shadow-2xl pb-8 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-border" />
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <span className="text-base font-bold text-foreground">Tambah Lead Baru</span>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground">
            <Icon name="XMarkIcon" size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 pt-4 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm">{error}</div>}

          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Nama Lengkap *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Budi Santoso"
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Nomor WhatsApp *</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="08123456789"
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              required
            />
          </div>

          {/* Project Selector */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1.5">Minat Project</label>
            {loadingProjects ? (
              <div className="w-full h-12 rounded-xl border border-border bg-muted animate-pulse" />
            ) : (
              <select
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 appearance-none"
              >
                <option value="">-- Pilih Project --</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            )}
          </div>

          {/* Kavling Selector — only shown after project selected */}
          {selectedProjectId && (
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">No. Kavling</label>
              {loadingPlots ? (
                <div className="w-full h-12 rounded-xl border border-border bg-muted animate-pulse" />
              ) : plots.length === 0 ? (
                <p className="text-xs text-muted-foreground px-1">Tidak ada kavling tersedia di project ini.</p>
              ) : (
                <select
                  value={selectedPlotId}
                  onChange={(e) => setSelectedPlotId(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 appearance-none"
                >
                  <option value="">-- Pilih No. Kavling (opsional) --</option>
                  {plots.map((p) => (
                    <option key={p.id} value={p.id}>
                      {getPlotLabel(p)}{p.status === 'HOLD' ? ' (Hold)' : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Points info */}
          <div className="flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-xl px-4 py-3">
            <span className="text-lg">⭐</span>
            <p className="text-xs text-primary font-semibold">+100 BTP poin akan diberikan setelah lead berhasil disimpan</p>
          </div>

          <button
            type="submit"
            disabled={saving || !name.trim() || !phone.trim()}
            className="w-full py-3.5 bg-primary text-white rounded-xl font-bold text-sm disabled:opacity-60 active:scale-95 transition-all"
          >
            {saving ? 'Menyimpan...' : '✓ Simpan Lead'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Points earned toast
function PointsToast({ onDismiss }: { onDismiss: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3500);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div className="fixed top-16 left-1/2 -translate-x-1/2 z-[80] flex items-center gap-2.5 bg-primary text-white px-5 py-3 rounded-2xl shadow-xl animate-bounce-in">
      <span className="text-xl">⭐</span>
      <div>
        <p className="text-sm font-bold">+100 BTP Poin!</p>
        <p className="text-xs opacity-80">Lead berhasil ditambahkan</p>
      </div>
    </div>
  );
}

const TABS = [
  { key: 'Semua', label: 'Semua' },
  { key: 'New', label: 'Baru' },
  { key: 'Contacted', label: 'Follow Up' },
  { key: 'Booking', label: 'Booking' },
];

export default function MobileLeadPage() {
  const { user, profile } = useAuth();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Semua');
  const [search, setSearch] = useState('');
  const [updateLead, setUpdateLead] = useState<Lead | null>(null);
  const [showAddLead, setShowAddLead] = useState(false);
  const [showPointsToast, setShowPointsToast] = useState(false);

  const supabase = createClient();

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    const fetchLeads = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('leads')
        .select('id, name, phone, stage, kavling_name, project_name, source, created_at')
        .eq('agent_id', user.id)
        .order('created_at', { ascending: false });
      if (data) setLeads(data as Lead[]);
      setLoading(false);
    };
    fetchLeads();

    const channel = supabase
      .channel('mobile_leads_page')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'leads', filter: `agent_id=eq.${user.id}` }, (payload) => {
        setLeads((prev) => [payload.new as Lead, ...prev]);
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'leads', filter: `agent_id=eq.${user.id}` }, (payload) => {
        setLeads((prev) => prev.map((l) => l.id === payload.new.id ? { ...l, ...(payload.new as Lead) } : l));
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user]);

  const filtered = leads.filter((l) => {
    const matchTab = activeTab === 'Semua' ? true : l.stage === activeTab;
    const matchSearch = l.name.toLowerCase().includes(search.toLowerCase()) ||
      (l.phone || '').includes(search) ||
      (l.kavling_name || '').toLowerCase().includes(search.toLowerCase());
    return matchTab && matchSearch;
  });

  const counts: Record<string, number> = {
    Semua: leads.length,
    New: leads.filter((l) => l.stage === 'New').length,
    Contacted: leads.filter((l) => l.stage === 'Contacted').length,
    Booking: leads.filter((l) => l.stage === 'Booking').length,
  };

  const handleStageUpdated = (id: string, stage: string) => {
    setLeads((prev) => prev.map((l) => l.id === id ? { ...l, stage } : l));
  };

  return (
    <MobilePartnerLayout showFloatingButton onFloatingButtonClick={() => setShowAddLead(true)}>
      {/* Points toast */}
      {showPointsToast && <PointsToast onDismiss={() => setShowPointsToast(false)} />}

      {/* Header */}
      <div className="bg-card border-b border-border px-4 pt-12 pb-3 sticky top-0 z-30">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-lg font-bold text-foreground">Lead Saya</h1>
          <button
            onClick={() => setShowAddLead(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white rounded-xl text-xs font-bold active:scale-95 transition-all"
          >
            <Icon name="PlusIcon" size={14} />
            Tambah
          </button>
        </div>
        {/* Search */}
        <div className="flex items-center gap-2 bg-muted rounded-xl px-3 py-2.5 mb-3">
          <Icon name="MagnifyingGlassIcon" size={18} className="text-muted-foreground flex-shrink-0" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari nama atau nomor..."
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
        </div>
        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-semibold transition-all ${
                activeTab === tab.key ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
              }`}
            >
              {tab.label}
              {counts[tab.key] > 0 && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${activeTab === tab.key ? 'bg-white/20 text-white' : 'bg-primary/10 text-primary'}`}>
                  {counts[tab.key]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div className="px-4 py-3 grid grid-cols-4 gap-2">
        {[
          { label: 'Total', value: leads.length, color: 'text-foreground' },
          { label: 'Baru', value: counts.New, color: 'text-blue-600' },
          { label: 'Follow Up', value: counts.Contacted, color: 'text-purple-600' },
          { label: 'Booking', value: counts.Booking, color: 'text-orange' },
        ].map((s) => (
          <div key={s.label} className="bg-card rounded-xl border border-border p-2.5 text-center">
            <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Lead list */}
      <div className="px-4 pb-4 space-y-3">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-card rounded-2xl border border-border p-4 animate-pulse">
              <div className="flex gap-3">
                <div className="w-10 h-10 rounded-full bg-muted flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-2/3" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              </div>
            </div>
          ))
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Icon name="UserGroupIcon" size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">Tidak ada lead ditemukan</p>
          </div>
        ) : (
          filtered.map((lead) => {
            const style = getStatusStyle(lead.stage);
            const initials = lead.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
            const waLink = lead.phone ? `https://wa.me/${lead.phone.replace(/\D/g, '')}` : `https://wa.me/?text=${encodeURIComponent(`Halo ${lead.name}`)}`;

            return (
              <div key={lead.id} className="bg-card rounded-2xl border border-border p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-primary">{initials}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-bold text-foreground">{lead.name}</p>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${style.cls}`}>{style.label}</span>
                    </div>
                    {lead.phone && <p className="text-xs text-muted-foreground mt-0.5">{lead.phone}</p>}
                    {(lead.project_name || lead.kavling_name) && (
                      <p className="text-xs text-muted-foreground">
                        {lead.project_name}{lead.kavling_name ? ` · ${lead.kavling_name}` : ''}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">Terakhir: {timeAgo(lead.created_at)}</p>
                  </div>
                </div>
                {/* Actions */}
                <div className="flex gap-2 mt-3">
                  <a
                    href={waLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-green-500 text-white rounded-xl text-xs font-bold active:scale-95 transition-all"
                  >
                    <Icon name="ChatBubbleLeftRightIcon" size={14} />
                    WhatsApp
                  </a>
                  <button
                    onClick={() => setUpdateLead(lead)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-primary/10 text-primary rounded-xl text-xs font-bold active:scale-95 transition-all"
                  >
                    <Icon name="PencilSquareIcon" size={14} />
                    Update
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {updateLead && (
        <UpdateStatusSheet
          lead={updateLead}
          onClose={() => setUpdateLead(null)}
          onUpdated={handleStageUpdated}
        />
      )}

      {showAddLead && (
        <AddLeadSheet
          onClose={() => setShowAddLead(false)}
          onAdded={(lead) => setLeads((prev) => [lead, ...prev])}
          agentId={user?.id || null}
          agentCode={profile?.agent_code || null}
          agentEmail={profile?.email || null}
          agentName={profile?.full_name || null}
          onPointsEarned={() => setShowPointsToast(true)}
        />
      )}
    </MobilePartnerLayout>
  );
}
